import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUser } from '@/hooks/use-user';
import { saveColumnPrefs } from '@/lib/column-prefs-persistence';
import { getVatLabelKey } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { isMaskableColumn } from '@/lib/utils/column-masking';
import { isSortableColumnVisible } from '@/lib/utils/column-visibility';
import { columnOrderState } from '@/states/column-order.state';
import { hiddenSortableColumnsState } from '@/states/column-visibility-user.state';
import { dealIdentifierState } from '@/states/deal-identifier.state';
import { displayModeState } from '@/states/display-mode.state';
import { hasCouponsInCurrentViewState } from '@/states/has-coupons-in-current-view.state';
import { hasDiscountedPriceOnDealAtom } from '@/states/has-discounted-price-on-deal.state';
import { hasShopifyIdsInCurrentViewState } from '@/states/has-shopify-ids-in-current-view.state';
import { kpiComparisonState } from '@/states/kpi-comparison.state';
import { storeAtom } from '@/states/store.state';
import { ColumnAccessorSortable } from '@/types/columns';
import Button from '@appchoose/button';
import { useAtom, useAtomValue } from 'jotai';
import { GripIcon } from 'lucide-react';

import {
  Sortable,
  SortableContent,
  SortableItem,
  SortableItemHandle,
} from './sortable';

interface ColumnOrderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function ColumnOrderModal({ open, onOpenChange }: ColumnOrderModalProps) {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const displayMode = useAtomValue(displayModeState);
  const kpiComparison = useAtomValue(kpiComparisonState);
  const hasCouponsInCurrentView = useAtomValue(hasCouponsInCurrentViewState);
  const hasAnyShopifyVariantId = useAtomValue(hasShopifyIdsInCurrentViewState);
  const hasDiscountedPrice = useAtomValue(hasDiscountedPriceOnDealAtom);
  const vatLabel = t(getVatLabelKey(store));

  const [columnOrder, setColumnOrder] = useAtom(columnOrderState);
  const [hiddenColumns, setHiddenColumns] = useAtom(hiddenSortableColumnsState);
  const hiddenColumnsSanitized = useMemo(
    () => hiddenColumns.filter(isMaskableColumn),
    [hiddenColumns]
  );
  const dealId = useAtomValue(dealIdentifierState);
  const user = useUser();

  const getColumnTitle = (columnAccessor: ColumnAccessorSortable) => {
    switch (columnAccessor) {
      case ColumnAccessorSortable.buyingPrice:
        return `${t('kpi.buying_price')} (${t('kpi.excl_vat')})`;
      case ColumnAccessorSortable.chooseDiscount:
        return t('kpi.choose_discount');
      case ColumnAccessorSortable.choosePrice:
        return `${t('kpi.choose_price')} (${vatLabel})`;
      case ColumnAccessorSortable.couponExpirationDate:
        return t('kpi.coupon_expiration_date');
      case ColumnAccessorSortable.couponHowToUse:
        return t('kpi.coupon_how_to_use');
      case ColumnAccessorSortable.couponTerms:
        return t('kpi.coupon_terms');
      case ColumnAccessorSortable.description:
        return t('kpi.description');
      case ColumnAccessorSortable.discountedPrice:
        return `${t('kpi.discounted_price')} (${vatLabel})`;
      case ColumnAccessorSortable.gtin:
        return t('kpi.gtin');
      case ColumnAccessorSortable.isBestSeller:
        return t('kpi.best_seller');
      case ColumnAccessorSortable.listPrice:
        return `${t('kpi.list_price')} (${vatLabel})`;
      case ColumnAccessorSortable.madeIn:
        return t('kpi.made_in');
      case ColumnAccessorSortable.marginPercentage:
        return t('kpi.margin_percentage');
      case ColumnAccessorSortable.potentialSalesVolume:
        return t('kpi.potential_sales_volume');
      case ColumnAccessorSortable.productCategory:
        return t('kpi.type');
      case ColumnAccessorSortable.productType:
        return t('kpi.product_type');
      case ColumnAccessorSortable.shippingPrice:
        return `${t('kpi.shipping_price')} (${vatLabel})`;
      case ColumnAccessorSortable.sku:
        return t('kpi.sku');
      case ColumnAccessorSortable.stockQuantity:
        return t('kpi.stock');
      case ColumnAccessorSortable.sourceUrl:
        return t('kpi.url');
      case ColumnAccessorSortable.vatRate:
        return t('kpi.vat');
      case ColumnAccessorSortable.shopifyVariantId:
        return t('kpi.shopify_variant_id');
      case ColumnAccessorSortable.eeePrice:
        return `${t('kpi.eee_price')}`;
    }
  };

  const businessVisibilityParams = useMemo(
    () => ({
      kpiComparison,
      hasCouponsInCurrentView,
      hasAnyShopifyVariantId,
      hasDiscountedPrice,
      store,
      isInternalUser: user?.isInternalUser ?? false,
      displayMode,
    }),
    [
      displayMode,
      hasAnyShopifyVariantId,
      hasCouponsInCurrentView,
      hasDiscountedPrice,
      kpiComparison,
      store,
      user,
    ]
  );

  const columns = useMemo(
    () =>
      columnOrder.filter((column) =>
        isSortableColumnVisible(column, businessVisibilityParams)
      ),
    [columnOrder, businessVisibilityParams]
  );

  const mergeOrderWithVisible = useCallback(
    (newVisibleOrder: ColumnAccessorSortable[]) => {
      const visibleSet = new Set(columns);
      const visibleIndices = columnOrder
        .map((col, i) => (visibleSet.has(col) ? i : -1))
        .filter((i) => i >= 0);
      const result = [...columnOrder];
      newVisibleOrder.forEach((col, i) => {
        result[visibleIndices[i]] = col;
      });
      setColumnOrder(result);
      if (dealId) saveColumnPrefs(dealId, result, hiddenColumnsSanitized);
    },
    [columnOrder, columns, setColumnOrder, dealId, hiddenColumnsSanitized]
  );

  const toggleColumnVisibility = useCallback(
    (column: ColumnAccessorSortable, visible: boolean) => {
      if (!isMaskableColumn(column)) return;

      const nextHidden = visible
        ? hiddenColumnsSanitized.filter((c) => c !== column)
        : [...hiddenColumnsSanitized, column];

      setHiddenColumns(nextHidden);
      if (dealId) saveColumnPrefs(dealId, columnOrder, nextHidden);
    },
    [hiddenColumnsSanitized, setHiddenColumns, dealId, columnOrder]
  );

  const isColumnVisible = useCallback(
    (column: ColumnAccessorSortable) =>
      !hiddenColumnsSanitized.includes(column),
    [hiddenColumnsSanitized]
  );

  return (
    <Dialog open={open} onOpenChange={() => onOpenChange(false)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-lg font-semibold">
            {t('components.column_order_modal.title')}
          </DialogTitle>
          <p className="text-center text-sm text-muted-foreground">
            {t('components.column_order_modal.hint')}
          </p>
        </DialogHeader>
        <div className="h-[500px] overflow-hidden">
          <section className="h-full overflow-auto pr-3">
            <Sortable
              value={columns}
              onValueChange={mergeOrderWithVisible}
              getItemValue={(item: ColumnAccessorSortable) => item}
            >
              <SortableContent>
                {columns.map((columnId) => (
                  <SortableItem
                    key={columnId}
                    value={columnId}
                    className="flex items-center gap-2 rounded-sm p-2 hover:bg-gray-100"
                  >
                    <SortableItemHandle
                      className="flex shrink-0 items-center justify-center rounded p-1 hover:bg-gray-200"
                      aria-label={getColumnTitle(columnId)}
                    >
                      <GripIcon className="size-4" />
                    </SortableItemHandle>
                    <p className="min-w-0 flex-1 truncate">
                      {getColumnTitle(columnId)}
                    </p>
                    <Checkbox
                      checked={isColumnVisible(columnId)}
                      disabled={!isMaskableColumn(columnId)}
                      onCheckedChange={(checked) => {
                        if (!isMaskableColumn(columnId)) return;
                        toggleColumnVisibility(columnId, checked === true);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className={cn('shrink-0')}
                      aria-label={
                        isMaskableColumn(columnId)
                          ? isColumnVisible(columnId)
                            ? t('components.column_order_modal.hide_column', {
                                column: getColumnTitle(columnId),
                              })
                            : t('components.column_order_modal.show_column', {
                                column: getColumnTitle(columnId),
                              })
                          : t('components.column_order_modal.show_column', {
                              column: getColumnTitle(columnId),
                            })
                      }
                    />
                  </SortableItem>
                ))}
              </SortableContent>
            </Sortable>
          </section>
        </div>
        <DialogFooter className="flex gap-3">
          <Button
            type="button"
            appearance="primary"
            onClick={() => onOpenChange(false)}
            className="flex items-center gap-2 border border-gray-300 font-medium"
          >
            {t('components.column_order_modal.done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ColumnOrderModal;
